import { useApiClient } from '@/composables/useApiClient'
import { useToast } from 'primevue'
import { useI18n } from 'vue-i18n'

import type { WebchatOrigin } from '@/components/whatsapp/types/interfaces'

export interface IFilterChat {
  user_id?: string
  status?: string
  page?: number
  size?: number
  /** Orden por fecha de llegada del chat; 'desc' trae primero el mas reciente. */
  order?: 'asc' | 'desc'
}

export interface Chat {
  id: string
  start_date: Date
  name: string
  /** null si el cliente adoptó username y solo se identifica por BSUID. */
  phone: string | null
  username?: string | null
  source_type: string
  channel_id: string
  origin?: WebchatOrigin | null
  campaign_id: string
  status: string
  end_date: null
  incoming_route_id: string
  provider_account_id: string
  destination: string
  messages: Message[]
  // No leidos de este agente en este chat, contados en la consulta: es lo que
  // hace que la insignia sobreviva a una recarga.
  unread_count: number
}

export interface ChatListResponse {
  assigned_chats: Chat[]
  pending_chats: Chat[]
  assigned_total: number
  pending_total: number
  page: number
  size: number
}

export interface Message {
  id: string
  date: Date
  status: string | null
  source_type: string
  content: unknown
  provider_response: unknown
  provider_message_id: string
  chat_id: string
  consecutive: number
  user_id: string | null
}

export const useChatService = () => {
  const privateApi = useApiClient(true)
  const toast = useToast()
  const { t } = useI18n()

  const showToast = (type: 'success' | 'error', messageKey: string) => {
    toast.add({
      severity: type,
      summary: t(type === 'success' ? 'general.success' : 'general.error'),
      detail: t(messageKey),
      life: 3000,
    })
  }

  const handleError = (error: unknown, messageKey: string) => {
    showToast('error', messageKey)
  }

  const getChats = async (
    query?: IFilterChat,
    options?: { showErrorToast?: boolean },
  ): Promise<ChatListResponse> => {
    try {
      return await privateApi.get<ChatListResponse>('/oper/chats', {
        params: { ...query },
      })
    } catch (error) {
      // El resync lo pide en tandas y con reintentos: un toast por intento
      // fallido convertiria una recuperacion silenciosa en una lluvia de avisos.
      if (options?.showErrorToast !== false) {
        handleError(error, 'whatsapp.error_loading_chats')
      }
      throw error
    }
  }

  const getChatHistory = async (
    chatId: string,
    params?: { limit?: number; before_message_id?: string },
  ): Promise<Message[]> => {
    try {
      return await privateApi.get<Message[]>(`/oper/chats/${chatId}/history`, { params })
    } catch (error) {
      handleError(error, 'whatsapp.error_loading_chat_history')
      throw error
    }
  }

  return {
    getChats,
    getChatHistory,
  }
}
